/**
 * Reports > Orders & Dispatch (user, 2026-10-30): SeedTrac's Order Booking and Dispatch reports, each
 * from its shared definition (ORDER_DISPATCH_REPORTS) on its own code. Filters live in the URL.
 */
import {
  ORDER_DISPATCH_REPORTS,
  ORDER_DISPATCH_REPORT_KEYS,
  ORDER_DISPATCH_REPORT_PERMISSION,
  type OrderDispatchReportKey,
} from "@nexseed/shared";
import {
  createFileRoute,
  notFound,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { ListPageSkeleton } from "@/components/app/skeletons";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import { PeriodReportPage } from "@/features/reports/period-report-page";
import { reportSearch } from "@/features/warehouse/reports/api";

const isKey = (k: string): k is OrderDispatchReportKey =>
  (ORDER_DISPATCH_REPORT_KEYS as string[]).includes(k);

export const Route = createFileRoute("/_app/reports/orders-dispatch/$key")({
  validateSearch: reportSearch,
  beforeLoad: ({ params }) => {
    if (!isKey(params.key)) throw notFound();
    if (!useAuth.getState().can(ORDER_DISPATCH_REPORT_PERMISSION[params.key]))
      throw redirect({ to: "/reports" });
  },
  component: Page,
});

function Page() {
  const { key } = Route.useParams();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { selected, ready } = useFacilities();
  if (!ready || !isKey(key)) return <ListPageSkeleton />;
  return (
    <PeriodReportPage
      key={key}
      def={ORDER_DISPATCH_REPORTS[key]}
      path={`/reports/orders-dispatch/${key}`}
      fileName={`${key}.xlsx`}
      facilityId={selected?.id}
      search={s}
      onSearch={(next) =>
        void navigate({ search: (prev) => ({ ...prev, ...next }) })
      }
    />
  );
}
